<template>
  <div class="ebook-container">
    <!-- Loading Indicator -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner"></div>
      <p>正在加载文档...</p>
    </div>

    <!-- PDF Viewer -->
    <div v-show="!isLoading" class="pdf-viewer-area">
      <div class="pdf-wrapper">
        <!-- 
          vue-pdf component to render the document.
          - :src: The source of the PDF file.
          - :page: The current page to display.
          - @num-pages: Event that emits the total number of pages.
          - @page-loaded: Event that emits when a page is successfully loaded.
          - @error: Event for handling loading errors.
        -->
        <pdf
          ref="pdf"
          style="width: 100%;"
          :src="pdfSrc"
          :page="currentPage"
          @num-pages="totalPages = $event"
          @page-loaded="handlePageLoaded"
          @error="handleError"
        ></pdf>
      </div>

      <!-- Controls -->
      <div class="controls">
        <button @click="prevPage" :disabled="currentPage <= 1">上一页</button>
        <span>第 {{ currentPage }} 页 / 共 {{ totalPages }} 页</span>
        <button @click="nextPage" :disabled="currentPage >= totalPages">下一页</button>
      </div>
    </div>
  </div>
</template>

<script>
import pdf from 'vue-pdf';
import debounce from 'lodash.debounce';

// Mock API function for saving progress. Replace with your actual API call.
const saveEbookProgress = (ebookId, page) => {
  console.log(`Saving progress for ebook ${ebookId}: page ${page}`);
  // Example using axios:
  // return axios.post(`/api/ebooks/${ebookId}/progress`, { page });
  return new Promise(resolve => setTimeout(resolve, 500)); // Simulate network delay
};

export default {
  name: 'EbookLearning',
  components: {
    pdf,
  },
  props: {
    // The unique identifier for the ebook to save progress
    ebookId: {
      type: [String, Number],
      required: true,
    },
    // The initial page to display, fetched from your backend
    initialPage: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      ebookInfo:'',
      pdfSrc: null,
      currentPage: 1,
      totalPages: 0,
      isLoading: true,
      debouncedSaveProgress: null, // To hold the debounced function
    };
  },
  created() {
    if(!this.$route.query.id){
        window.location.href='/course?type=1'
        return false;
    }
    this.id = this.$route.query.id;
    this.getData();
  },
  watch: {
    // Watch for changes on currentPage. When the user turns the page,
    // call the debounced function to save the progress.
    currentPage(newPage, oldPage) {
      if (newPage > 0 && newPage !== oldPage) {
        this.debouncedSaveProgress(this.ebookId, newPage);
      }
    },
  },
  methods: {
    async getData(){
        const res = await this.$http.get('/api/course/getCourseDetail',{id:this.id});
        console.log(res,'bookinfo')
            if(res){
                this.ebookInfo = res;
                this.pdfUrl = res.attach;
                // this.pdfUrl = '/uploads/20260324/9f3c00d04077f6d8c65a02bc22b6ffec.pdf'
                // When the component is created, initialize the PDF source using a loading task.
                // This is the recommended way by vue-pdf to handle remote files.
                if (this.pdfUrl) {
                    const cMapUrl = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.14.305/cmaps/';
                    this.pdfSrc = pdf.createLoadingTask({
                        url: this.pdfUrl,
                        cMapUrl,
                        cMapPacked: true,
                    });
                }
                // Set the current page to the last viewed page passed via props.
                this.currentPage = this.initialPage;
                this.debouncedSaveProgress = debounce(this.saveProgress, 1500);
            }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage -= 1;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage += 1;
      }
    },
    handlePageLoaded(pageNumber) {
      this.isLoading = false;
      console.log(`Page ${pageNumber} loaded.`);
      // For implementing a page-turning effect like a slide or fade,
      // you could wrap the <pdf> component in a <transition> tag
      // and change the :key attribute on page change.
      // Example: <transition name="fade"><pdf :key="currentPage" ... /></transition>
    },
    handleError(error) {
      this.isLoading = false;
      console.error('Failed to load PDF:', error);
      // You could display a user-friendly error message here.
    },
    saveProgress(ebookId, page) {
      // Call the mock API function.
      saveEbookProgress(ebookId, page)
        .then(() => {
          console.log('Progress saved successfully.');
        })
        .catch(err => {
          console.error('Failed to save progress:', err);
        });
    },
  },
};
</script>

<style scoped>
.ebook-container {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f0f2f5;
  position: relative;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 10;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #09f;
  animation: spin 1s ease infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.pdf-viewer-area {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.pdf-wrapper {
  flex-grow: 1;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  padding: 20px;
  min-height: 0;
}

/* Styling for the vue-pdf canvas for better presentation */
.pdf-wrapper >>> .vue-pdf-embed,
.pdf-wrapper >>> canvas {
  max-width: 100%;
  height: auto !important; /* Override default height */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.controls {
  flex-shrink: 0;
  padding: 10px;
  background-color: #fff;
  border-top: 1px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
}

.controls button {
  padding: 8px 16px;
  border: 1px solid #ccc;
  background-color: #fff;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.controls button:hover:not(:disabled) {
  background-color: #f0f0f0;
}

.controls button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.controls span {
  font-size: 14px;
  color: #333;
  min-width: 120px;
  text-align: center;
}
</style>
